// web/src/screens/Billing.tsx — Website care, $100/month or $1,000/year (portal spec 15). One card per site.
// "Start your plan" (monthly/annual picker → Stripe Checkout, card on file, first charge 30 days after go-live),
// each site's plan, the Stripe portal (change card, switch plan, cancel at period end), invoices.
// The update-credit meter and top-up packs belong to the hidden chalice engine (portal spec 10) and only render with
// VITE_CHALICE_ENABLED (D7: one plan, nothing else sold).
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Invoice, PlanInterval, SitePlan } from '../lib/types';
import { money, dateOf } from '../lib/status';
import { PageHeader } from '../components/PageHeader';
import { AsyncBoundary } from '../components/AsyncBoundary';
import { Skeleton } from '../components/Skeleton';
import { DataTable } from '../components/DataTable';
import { CreditMeter } from '../components/CreditMeter';
import { TopUpPacks } from '../components/TopUpPacks';
import { Icon } from '../components/Icon';
import { toast } from '../components/Toast';
import { FEATURES } from '../lib/features';

const PER: Record<PlanInterval, string> = { month: 'a month', year: 'a year' };

function planLine(p: SitePlan): { label: string; cls: string; note: string } {
  if (p.cancelAtPeriodEnd) return { label: 'Ending', cls: 'text-warn border-warn', note: p.currentPeriodEnd ? `Your site stays up until ${dateOf(p.currentPeriodEnd)}.` : '' };
  switch (p.status) {
    case 'trialing': return { label: 'Included for now', cls: 'text-info border-info', note: 'Your first charge is 30 days after your site goes live.' };
    case 'active': return { label: 'Active', cls: 'text-run border-run', note: p.currentPeriodEnd ? `Renews ${dateOf(p.currentPeriodEnd)}` : '' };
    case 'past_due':
    case 'unpaid': return { label: 'Payment failed', cls: 'text-danger border-danger', note: 'Please update your card to keep your site online.' };
    case 'incomplete': return { label: 'Card needed', cls: 'text-warn border-warn', note: 'Please finish adding your card.' };
    default: return { label: p.status, cls: 'text-muted border-hair', note: '' };
  }
}

/** Monthly / annual choice. Also used by the go-live checklist (spec 14). */
export function PlanPicker({ amounts, value, onChange }: {
  amounts: Record<PlanInterval, number>; value: PlanInterval; onChange: (v: PlanInterval) => void;
}) {
  const opt = (v: PlanInterval, title: string, sub: string) => (
    <button type="button" onClick={() => onChange(v)} aria-pressed={value === v}
      className={`min-h-11 flex-1 rounded-md border px-4 py-2 text-left ${value === v ? 'border-run bg-run/10' : 'border-hair'}`}>
      <div className="font-head text-ink">{title}</div><div className="text-xs text-muted">{sub}</div>
    </button>
  );
  return (
    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
      {opt('month', `${money(amounts.month)} a month`, 'Cancel any time')}
      {opt('year', `${money(amounts.year)} a year`, 'Two months free')}
    </div>
  );
}

function Credits() {
  const credits = useQuery({ queryKey: ['credits'], queryFn: () => api.getCredits() });
  return (
    <section className="mb-6 flex flex-col gap-4">
      <h2 className="font-head text-sm text-muted">Your credits</h2>
      {credits.data
        ? <CreditMeter used={credits.data.creditsUsed} granted={credits.data.creditsGranted}
            resetsAt={credits.data.resetsAt} killed={credits.data.killed} />
        : <Skeleton variant="card" count={1} />}
      {!credits.data?.killed && <TopUpPacks />}
    </section>
  );
}

export function Billing() {
  const q = useQuery({ queryKey: ['billing'], queryFn: () => api.getBilling() });
  const [busy, setBusy] = useState(false);
  const [interval, setInterval] = useState<PlanInterval>('month');
  const go = async (fn: () => Promise<{ url: string }>) => {
    setBusy(true);
    try { window.location.href = (await fn()).url; }
    catch (e) { toast({ kind: 'error', message: (e as Error).message || 'Something went wrong. Please try again.' }); }
    finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader title="Billing" subtitle="Website care: hosting, security, changes when you ask, and a new page every month." />
      {FEATURES.chalice && <Credits />}
      <AsyncBoundary query={q} skeleton={<Skeleton variant="card" count={2} />}>
        {(b) => (
          <div className="flex flex-col gap-6">
            {b.canStart.map((s) => (
              <div key={s.siteId} className="rounded-lg border border-hair border-l-4 border-l-run bg-surface p-5">
                <h2 className="font-head text-ink">Start your plan for {s.siteName}</h2>
                <p className="mt-1 text-sm text-muted">Your first 30 days after launch are included. Your first charge is 30 days after your site goes live.</p>
                <PlanPicker amounts={b.amounts} value={interval} onChange={setInterval} />
                <button disabled={busy || !b.configured} onClick={() => go(() => api.checkout(s.siteId, interval))}
                  className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-md bg-run px-4 font-medium text-runink disabled:opacity-50">
                  <Icon name="rocket" size={16} />Add my card
                </button>
                {!b.configured && <p className="mt-2 text-xs text-muted">Billing isn’t switched on yet. We’ll let you know.</p>}
              </div>
            ))}
            {b.plans.map((p) => { const l = planLine(p); return (
              <div key={p.siteId} className="rounded-lg border border-hair bg-surface p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-muted">{p.siteName}</div>
                    <div className="mt-1 font-head text-lg text-ink">Website care · {money(b.amounts[p.interval])} {PER[p.interval]}</div>
                  </div>
                  <span className={`shrink-0 rounded-pill border px-3 py-1 text-xs ${l.cls}`}>{l.label}</span>
                </div>
                {l.note && <p className="mt-2 text-xs text-muted">{l.note}</p>}
              </div>
            ); })}
            {b.plans.length > 0 && (
              <div>
                <button disabled={busy} onClick={() => go(() => api.portal())}
                  className="inline-flex min-h-11 items-center gap-2 rounded-md border border-hair px-4 text-sm text-ink disabled:opacity-50">
                  <Icon name="credit-card" size={15} />Change card, switch plan or cancel
                </button>
                <p className="mt-2 text-xs text-muted">Opens Stripe’s secure page. Cancelling takes effect at the end of the period you’ve paid for.</p>
              </div>
            )}
            {b.plans.length === 0 && b.canStart.length === 0 && (
              <p className="rounded-lg border border-hair bg-surface/40 px-4 py-6 text-center text-sm text-muted">No websites to set up yet.</p>
            )}
            <section>
              <h2 className="mb-3 font-head text-sm text-muted">Invoices</h2>
              {b.invoices.length
                ? <DataTable<Invoice> rows={b.invoices} rowKey={(i) => i.id}
                    columns={[
                      { key: 'date', header: 'Date', render: (i) => dateOf(i.date) },
                      { key: 'amount', header: 'Amount', render: (i) => <span className="font-head">{money(i.amount)}</span> },
                      { key: 'status', header: 'Status', render: (i) => <span className={i.status === 'paid' ? 'text-run' : i.status === 'failed' ? 'text-danger' : 'text-warn'}>{i.status}</span> },
                      { key: 'url', header: '', render: (i) => i.url ? <a href={i.url} className="text-info">Download</a> : null },
                    ]}
                    mobileCard={(i) => (
                      <div className="flex items-center justify-between rounded-lg border border-hair bg-surface p-3">
                        <div><div className="font-head text-sm text-ink">{money(i.amount)}</div><div className="text-xs text-muted">{dateOf(i.date)}</div></div>
                        <span className={`text-xs ${i.status === 'paid' ? 'text-run' : 'text-warn'}`}>{i.status}</span>
                      </div>
                    )} />
                : <p className="rounded-lg border border-hair bg-surface/40 px-4 py-6 text-center text-sm text-muted">No invoices yet.</p>}
            </section>
          </div>
        )}
      </AsyncBoundary>
    </>
  );
}
